<template>
<div
    v-if="visible"
    class="vertical-control-bar"
>
    <!-- Preview Controls Label -->
    <div class="preview-label">
        Preview
    </div>
    <!-- Show/Hide Toggle -->
    <div class="control-section expandable-section">
        <!-- Always show current state -->
        <v-btn
            x-small
            color="error"
            class="control-btn primary-btn"
            :title="lightbox_visible ? 'Hide Lightbox' : 'Show Lightbox'"
        >
            <v-icon x-small>{{ lightbox_visible ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
        </v-btn>

        <!-- Show opposite option on hover -->
        <v-btn
            x-small
            color="grey"
            outlined
            @click="lightbox_visible ? $emit('hide-lightbox') : $emit('show-lightbox')"
            class="control-btn hover-btn"
            :title="lightbox_visible ? 'Hide Lightbox' : 'Show Lightbox'"
        >
            <v-icon x-small>{{ lightbox_visible ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
        </v-btn>
    </div>

    <!-- Desktop/Mobile Toggle -->
    <div class="control-section expandable-section">
        <!-- Always show current mode -->
        <v-btn
            x-small
            color="error"
            class="control-btn primary-btn"
            :title="preview_mode === 'desktop' ? 'Desktop View' : 'Mobile View'"
        >
            <v-icon x-small>{{ preview_mode === 'desktop' ? 'mdi-monitor' : 'mdi-cellphone' }}</v-icon>
        </v-btn>

        <!-- Show opposite mode on hover -->
        <v-btn
            x-small
            color="grey"
            outlined
            @click="onPreviewModeChange(preview_mode === 'desktop' ? 'mobile' : 'desktop')"
            class="control-btn hover-btn"
            :title="preview_mode === 'desktop' ? 'Mobile View' : 'Desktop View'"
        >
            <v-icon x-small>{{ preview_mode === 'desktop' ? 'mdi-cellphone' : 'mdi-monitor' }}</v-icon>
        </v-btn>
    </div>

    <!-- Page Navigation -->
    <div class="control-section expandable-section">
        <!-- Always show current page -->
        <v-btn
            x-small
            color="error"
            class="page-btn primary-btn"
            :title="current_page === 'form' ? 'Form Page' : 'Thank You Page'"
        >
            <v-icon x-small>{{ current_page === 'form' ? 'mdi-form-select' : 'mdi-heart' }}</v-icon>
        </v-btn>

        <!-- Show opposite page on hover -->
        <v-btn
            x-small
            color="grey"
            outlined
            @click="current_page === 'form' ? $emit('show-thank-you') : $emit('show-lightbox')"
            class="page-btn hover-btn"
            :title="current_page === 'form' ? 'Show Thank You Page' : 'Show Form'"
        >
            <v-icon x-small>{{ current_page === 'form' ? 'mdi-heart' : 'mdi-form-select' }}</v-icon>
        </v-btn>
    </div>

    <!-- Close Button -->
    <div class="control-section">
        <v-btn
            icon
            x-small
            @click="onDismiss"
            class="close-btn"
            title="Hide Controls"
        >
            <v-icon x-small>mdi-close</v-icon>
        </v-btn>
    </div>
</div>
</template>

<script>
export default {
    name: 'MLightboxFloatingPreviewControl',
    
    props: {
        visible: {
            type: Boolean,
            default: true
        },
        preview_mode: {
            type: String,
            default: 'desktop'
        },
        current_page: {
            type: String,
            default: 'form' // 'form' or 'thanks'
        },
        lightbox_visible: {
            type: Boolean,
            default: true
        }
    },

    methods: {
        onPreviewModeChange(mode) {
            this.$emit('preview-mode-change', mode);
        },

        onDismiss() {
            this.$emit('dismiss');
        }
    }
}
</script>

<style scoped>
.vertical-control-bar {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    background: rgba(254, 247, 247, 0.95);
    border: 1px solid #ffcdd2;
    border-radius: 8px;
    backdrop-filter: blur(4px);
    box-shadow: 0 2px 8px rgba(244, 67, 54, 0.1);
    transition: box-shadow 0.2s ease;
    user-select: none;
    z-index: 1000;
}

.vertical-control-bar:hover {
    background: rgba(254, 247, 247, 1);
    box-shadow: 0 4px 12px rgba(244, 67, 54, 0.15);
}

.preview-label {
    font-size: 8px;
    color: rgba(244, 67, 54, 0.8);
    text-align: center;
    padding: 2px 1px;
    border-bottom: 1px solid rgba(244, 67, 54, 0.1);
    margin-bottom: 2px;
    font-weight: 500;
    line-height: 1.1;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
}

.control-section {
    padding: 4px 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    position: relative;
}

.control-section:not(:last-child) {
    border-bottom: 1px solid rgba(244, 67, 54, 0.1);
}

.expandable-section {
    overflow: visible;
}

.expandable-section .hover-btn {
    position: absolute;
    top: 4px;
    right: 42px;
    opacity: 0;
    transform: translateX(8px) scale(0.9);
    transition: all 0.2s ease;
    pointer-events: none;
    z-index: 10;
}

/* Create a bridge area to prevent hover gaps */
.expandable-section::after {
    content: '';
    position: absolute;
    top: 4px;
    right: 32px;
    width: 50px;
    height: 28px;
    background: transparent;
    z-index: 5;
    pointer-events: none;
}

.expandable-section:hover .hover-btn {
    opacity: 1;
    transform: translateX(0) scale(1);
    pointer-events: auto;
}

.expandable-section:hover::after {
    pointer-events: auto;
}

.expandable-section:hover {
    z-index: 20;
}

.expandable-section .primary-btn {
    transition: none; /* Prevent primary button from moving */
}

.primary-btn {
    min-width: 32px !important;
    width: 32px !important;
    height: 28px !important;
    padding: 0 !important;
    margin: 0 !important;
}

.hover-btn {
    min-width: 32px !important;
    width: 32px !important;
    height: 28px !important;
    padding: 0 !important;
    margin: 0 !important;
    background: rgba(255, 255, 255, 0.98) !important;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(244, 67, 54, 0.3) !important;
}

.hover-btn:hover {
    background: rgba(254, 247, 247, 1) !important;
    box-shadow: 0 4px 16px rgba(244, 67, 54, 0.25) !important;
}

.control-btn, .page-btn {
    border-radius: 6px !important;
}

.close-btn {
    color: rgba(244, 67, 54, 0.7) !important;
    transition: all 0.2s ease;
    min-width: 32px !important;
    width: 32px !important;
    height: 28px !important;
}

.close-btn:hover {
    color: #f44336 !important;
    background: rgba(244, 67, 54, 0.1) !important;
}

/* Responsive adjustments */
@media only screen and (max-width: 920px) {
    .vertical-control-bar {
        display: none;
    }
}

/* Extra compact for smaller screens */
@media only screen and (max-width: 1200px) {
    .vertical-control-bar {
        width: 36px;
        right: 15px;
    }

    .primary-btn, .hover-btn {
        min-width: 28px !important;
        width: 28px !important;
        height: 24px !important;
    }

    .close-btn {
        min-width: 28px !important;
        width: 28px !important;
        height: 24px !important;
    }

    .control-section {
        padding: 3px 1px;
    }

    .expandable-section .hover-btn {
        right: 38px;
    }

    .expandable-section::after {
        right: 28px;
        width: 46px;
    }

    .preview-label {
        font-size: 7px;
        padding: 1px;
        letter-spacing: 0.1px;
    }
}
</style>
